// src/components/InstallAppButton.jsx
import React, { useEffect, useMemo, useState } from "react";

export default function InstallAppButton({
  className = "px-4 py-1.5 rounded-full border border-indigo-900 text-indigo-900 hover:bg-indigo-50 font-semibold",
  label = "DOWNLOAD APP",
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  }, []);

  useEffect(() => {
    // Detect already-installed (standalone mode)
    const checkInstalled = () => {
      try {
        const standalone =
          window.matchMedia?.("(display-mode: standalone)")?.matches ||
          window.navigator.standalone === true;
        setInstalled(Boolean(standalone));
      } catch {}
    };
    checkInstalled();

    const onBeforeInstallPrompt = (e) => {
      // Let us show the prompt via our button
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const onClick = async () => {
    // If browser supports the install prompt:
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        // Wait for user choice
        await deferredPrompt.userChoice;
      } catch {
        // ignore
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }

    // iOS fallback (cannot trigger prompt programmatically)
    if (isIOS) {
      alert(
        'To install: tap Share (⬆️) → "Add to Home Screen".'
      );
      return;
    }

    // Generic fallback
    alert("To install: open browser menu → Install app / Add to Home Screen.");
  };

  if (installed) {
    return (
      <button type="button" className={className} disabled style={{ opacity: 0.6 }}>
        INSTALLED ✅
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {label}
    </button>
  );
}